/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconBell,
  IconCreditCard,
  IconMenu2,
  IconPackage,
  IconSettings,
  IconShoppingCart,
} from "@tabler/icons-react";
import { NotificationCenter } from "../../../../components/common/app-shell/notification-center";
import { InboxBellBadge } from "../../../inbox/presentation/components/inbox-bell-badge";

import { Button } from "../../../../components/common/button";
import { AppRoutes } from "../../../../common/routes/app_routes";
import type { UserDto } from "../../../../api";
import { Role } from "../../../../api";
import { CartIcon } from "../../../cart/presentation/components/cart-icon";
import { CartDrawer } from "../../../cart/presentation/components/cart-drawer";
import { useMePartner } from "../../../../swr";
import { getUserInitials, UserMenu, type UserMenuEntry } from "../../../../components/common/app-shell/user-menu";
import { UserAvatar } from "../../../../components/common/app-shell/user-avatar";
import { usePurchaseOrderStats } from "../../../purchase-orders/hooks/usePurchaseOrderStats";

type HeaderProps = {
  user?: UserDto;
  mutate?: (data: any) => void;
  setSidebarOpen?: (open: boolean) => void;
  handleLogout: () => void;
};

const SELLER_MENU_ENTRIES: UserMenuEntry[] = [
  {
    label: "My Products",
    icon: <IconPackage size={16} />,
    to: AppRoutes.MY_PRODUCTS,
  },
  {
    label: "Orders",
    icon: <IconShoppingCart size={16} />,
    to: AppRoutes.PURCHASE_ORDERS,
  },
  {
    label: "Payments",
    icon: <IconCreditCard size={16} />,
    to: AppRoutes.PAYMENTS,
  },
  {
    label: "Settings",
    icon: <IconSettings size={16} />,
    to: AppRoutes.SETTINGS,
  },
];

export function Header({ user, setSidebarOpen, handleLogout }: HeaderProps) {
  const navigate = useNavigate();
  const [cartOpen, setCartOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  const { isBuyer } = useMePartner();
  const { data: purchaseOrderStats } = usePurchaseOrderStats();
  const isAdmin = user?.role === Role.Admin;
  const showCart = isBuyer && !isAdmin;
  const initials = getUserInitials(user?.name);
  const iconButtonClassName =
    "relative inline-flex size-10 items-center justify-center text-textBody transition-colors hover:text-textDark";

  const handleCartOpen = useCallback(() => {
    setCartOpen(true);
  }, []);

  const handleCartClose = useCallback(() => {
    setCartOpen(false);
  }, []);

  const renderContent = () => {
    if (!user) {
      return (
        <div className="flex-1 flex justify-end">
          <Button variant="primary"
            onClick={() => navigate(AppRoutes.LOGIN)}
            className="w-[150px]"
          >
            Login
          </Button>
        </div>
      );
    }

    return (
      <div className="flex-1 flex items-center justify-end gap-3">
        {showCart && <CartIcon onClick={handleCartOpen} />}
        <div className="relative">
          <button
            ref={bellRef}
            type="button"
            onClick={() => setNotifOpen((v) => !v)}
            aria-label="Notifications"
            aria-expanded={notifOpen}
            className={iconButtonClassName}
          >
            <IconBell size={24} />
            <InboxBellBadge />
          </button>
          <NotificationCenter
            isOpen={notifOpen}
            onClose={() => setNotifOpen(false)}
            anchorRef={bellRef}
          />
        </div>
        <UserMenu
          name={user.name ?? ""}
          email={user.email ?? ""}
          initials={initials}
          entries={
            isAdmin
              ? []
              : SELLER_MENU_ENTRIES.map((entry) =>
                  entry.label === "Orders"
                    ? {
                        ...entry,
                        badgeCount: purchaseOrderStats?.processingPurchaseOrders ?? 0,
                      }
                    : entry,
                )
          }
          onLogout={handleLogout}
          trigger={<UserAvatar initials={initials} size="md" />}
        />
      </div>
    );
  };

  return (
    <>
      <header className="sticky top-0 z-40 w-full border-b border-borderLight bg-bgWhite print:hidden">
        <div className="flex items-center px-8 py-3">
          <button
            type="button"
            onClick={() => setSidebarOpen?.(true)}
            className="mr-2 p-2 text-textBody hover:text-textDark lg:hidden"
            aria-label="Open sidebar"
          >
            <IconMenu2 size={22} />
          </button>
          {renderContent()}
        </div>
      </header>
      {showCart && <CartDrawer open={cartOpen} onClose={handleCartClose} />}
    </>
  );
}
